import { Vector } from './helpers/Vector';
import type { DataValue, FunctionPortDefinition, IntegrationAccess } from '@glueapp/graph-types';
import {
    DataConnection,
    FunctionDefinition,
    GlueVariable,
    NodeInstance,
    NodeInstanceDataPort,
    NodeInstanceState,
    UserDefinedType,
} from '@glueapp/graph-types';
export const ADD_NODE = 'ADD_NODE';
export const MERGE_IN_GRAPH = 'MERGE_IN_GRAPH';
export const MOVE_NODES = 'MOVE_NODES';
export const SET_NODE_STATE = 'SET_NODE_STATE';
export const SET_NODE_SHAPE = 'SET_NODE_SHAPE';
export const MOVE_NODES_END = 'MOVE_NODES_END';
export const SET_NODE_SELECTED_INTEGRATION_ACCESS = 'SET_NODE_SELECTED_INTEGRATION_ACCESS';
export const REMOVE_NODES = 'REMOVE_NODES';
export const SET_PORT_VALUE_AND_TYPE = 'SET_PORT_VALUE_AND_TYPE';
export const SET_PORT_USER_DEFINED_TYPE = 'SET_PORT_USER_DEFINED_TYPE';

export const RESIZE_NODE = 'RESIZE_NODE';
export const RESIZE_NODE_END = 'RESIZE_NODE_END';
export const SET_NODE_DIMENSIONS = 'SET_NODE_DIMENSIONS';

export const ADD_CONNECTION = 'ADD_CONNECTION';
export const REMOVE_CONNECTION = 'REMOVE_CONNECTION';

export const SELECT_NODES = 'SELECT_NODES';
export const SELECT_BRANCH = 'SELECT_BRANCH';
export const SELECT_BRANCH_ADD = 'SELECT_BRANCH_ADD';
export const DESELECT_NODES = 'DESELECT_NODES';
export const SELECT_ONLY_ONE_NODE = 'SELECT_ONLY_ONE_NODE';
export const DESELECT_ALL_NODES = 'DESELECT_ALL_NODES';

export const ADD_VARIABLE = 'ADD_VARIABLE';
export const UPDATE_VARIABLE = 'UPDATE_VARIABLE';
export const DELETE_VARIABLE = 'DELETE_VARIABLE';

export const SET_SELECTED_VARIABLE = 'SET_SELECTED_VARIABLE';

export const LAYOUT_GRAPH = 'LAYOUT_GRAPH';

export const ADD_FUNCTION = 'ADD_FUNCTION';
export const UPDATE_FUNCTION = 'UPDATE_FUNCTION';
export const UPDATE_FUNCTION_NAME = 'UPDATE_FUNCTION_NAME';
export const DELETE_FUNCTION = 'DELETE_FUNCTION';
export const SET_FUNCTION_PORT_VALUE = 'SET_FUNCTION_PORT_VALUE';
export const CREATE_FUNCTION_WITH_INSTANCE = 'CREATE_FUNCTION_WITH_INSTANCE';
export const CREATE_FUNCTION_FROM_SIGNATURE = 'CREATE_FUNCTION_FROM_SIGNATURE';
export const ADD_FUNCTION_INSTANCE = 'ADD_FUNCTION_INSTANCE';
export const UPDATE_FUNCTION_INPUTS = 'UPDATE_FUNCTION_INPUTS';
export const UPDATE_FUNCTION_OUTPUTS = 'UPDATE_FUNCTION_OUTPUTS';

export const SET_VARIABLES = 'SET_VARIABLES';
export const REMOVE_CONNECTION_BY_ENDPOINTS = 'REMOVE_CONNECTION_BY_ENDPOINTS';

export const setNodeState = (
    id: number,
    state: NodeInstanceState,
    functionId?: string,
): { type: 'SET_NODE_STATE'; id: number; state: NodeInstanceState; functionId?: string } => ({
    type: SET_NODE_STATE,
    id,
    state,
    functionId,
});

export const setNodeShape = (
    id: number,
    shape: { inPorts: NodeInstanceDataPort[]; outPorts: NodeInstanceDataPort[] },
    functionId?: string,
): {
    type: 'SET_NODE_SHAPE';
    id: number;
    shape: { inPorts: NodeInstanceDataPort[]; outPorts: NodeInstanceDataPort[]; functionId?: string };
    functionId?: string;
} => ({
    type: SET_NODE_SHAPE,
    id,
    shape,
    functionId,
});

export const moveNodes = (
    delta: Vector,
    selectedLegacyIds: number[],
    functionId?: string,
): { type: 'MOVE_NODES'; delta: Vector; selectedLegacyIds: number[]; functionId?: string } => ({
    type: MOVE_NODES,
    delta,
    selectedLegacyIds,
    functionId,
});

export const setNodeSelectedIntegrationAccess = (
    id: number,
    integrationAccess: IntegrationAccess,
    functionId?: string,
): { type: 'SET_NODE_SELECTED_INTEGRATION_ACCESS'; id: number; integrationAccess: IntegrationAccess; functionId?: string } => ({
    type: SET_NODE_SELECTED_INTEGRATION_ACCESS,
    id,
    integrationAccess,
    functionId,
});

export const moveNodesEnd = (functionId?: string): { type: 'MOVE_NODES_END'; functionId?: string } => ({
    type: MOVE_NODES_END,
    functionId,
});

export const addConnection = (
    connection: Omit<DataConnection, 'id'>,
    functionId?: string,
): { type: 'ADD_CONNECTION'; connection: Omit<DataConnection, 'id'>; functionId?: string } => ({
    type: ADD_CONNECTION,
    connection,
    functionId,
});

export const removeConnection = (
    index: number,
    functionId?: string,
): { type: 'REMOVE_CONNECTION'; index: number; functionId?: string } => ({
    type: REMOVE_CONNECTION,
    index,
    functionId,
});

export const addNode = (
    nodeTemplateId: string,
    position: Vector,
    functionId?: string,
): { type: 'ADD_NODE'; nodeTemplateId: string; position: Vector; functionId?: string } => ({
    type: ADD_NODE,
    nodeTemplateId,
    position,
    functionId,
});

/**
 * Merge a pre-translated set of nodes and connections into the current graph.
 *
 * Contract: the caller has already renumbered every `node.id` to a fresh
 * (snapshot.max + 1, +2, ...) value, mapped each connection's
 * `sendingNodeInstanceId` / `receivingNodeInstanceId` to the new ids, assigned
 * fresh `connection.id`s the same way, and stripped any transient state keys
 * (per the node template's `transientStateKeys`). The dispatcher writes these
 * verbatim into the Y.Doc — it does not re-translate.
 *
 * Construct via `dispatchMergeInGraph` in GraphEditor.tsx, which performs the
 * pre-translation and returns the assigned ids so callers can react to them.
 */
export const mergeInGraph = (
    nodes: NodeInstance[],
    connections: DataConnection[],
    deselectOld: boolean,
    functionId?: string,
): { type: 'MERGE_IN_GRAPH'; nodes: NodeInstance[]; connections: DataConnection[]; deselectOld: boolean; functionId?: string } => ({
    type: MERGE_IN_GRAPH,
    nodes,
    connections,
    deselectOld,
    functionId,
});

export const removeNode = (ids: number[], functionId?: string): { type: 'REMOVE_NODES'; ids: number[]; functionId?: string } => ({
    type: REMOVE_NODES,
    ids,
    functionId,
});

export const setPortValueAndType = (
    nodeInstanceId: number,
    port: NodeInstanceDataPort,
    value: DataValue | undefined,
    userDefinedType?: UserDefinedType,
    functionId?: string,
): {
    type: 'SET_PORT_VALUE_AND_TYPE';
    nodeInstanceId: number;
    port: NodeInstanceDataPort;
    value: DataValue | undefined;
    userDefinedType?: UserDefinedType;
    functionId?: string;
} => ({
    type: SET_PORT_VALUE_AND_TYPE,
    nodeInstanceId,
    port,
    value,
    userDefinedType,
    functionId,
});

export const setPortUserDefinedType = (
    nodeInstanceId: number,
    portName: string,
    userDefinedType: UserDefinedType | undefined,
    functionId?: string,
): {
    type: 'SET_PORT_USER_DEFINED_TYPE';
    nodeInstanceId: number;
    portName: string;
    userDefinedType: UserDefinedType | undefined;
    functionId?: string;
} => ({
    type: SET_PORT_USER_DEFINED_TYPE,
    nodeInstanceId,
    portName,
    userDefinedType,
    functionId,
});

export const resizeNode = (
    nodeId: number,
    position: Vector,
    width: number,
    height: number,
    functionId?: string,
): {
    type: 'RESIZE_NODE';
    nodeId: number;
    position: Vector;
    width: number;
    height: number;
    functionId?: string;
} => ({
    type: RESIZE_NODE,
    nodeId,
    position,
    width,
    height,
    functionId,
});

export const resizeNodeEnd = (
    functionId?: string,
): {
    type: 'RESIZE_NODE_END';
    functionId?: string;
} => ({
    type: RESIZE_NODE_END,
    functionId,
});

export const setNodeDimensions = (
    nodeId: number,
    dimensions: Vector,
    functionId?: string,
): {
    type: 'SET_NODE_DIMENSIONS';
    nodeId: number;
    dimensions: Vector;
    functionId?: string;
} => ({
    type: SET_NODE_DIMENSIONS,
    nodeId,
    dimensions,
    functionId,
});

export const selectBranch = (id: number, functionId?: string): { type: 'SELECT_BRANCH'; id: number; functionId?: string } => ({
    type: SELECT_BRANCH,
    id,
    functionId,
});

export const selectBranchAdd = (id: number, functionId?: string): { type: 'SELECT_BRANCH_ADD'; id: number; functionId?: string } => ({
    type: SELECT_BRANCH_ADD,
    id,
    functionId,
});

export const selectNodes = (ids: number[], functionId?: string): { type: 'SELECT_NODES'; ids: number[]; functionId?: string } => ({
    type: SELECT_NODES,
    ids,
    functionId,
});

export const deselectNodes = (ids: number[], functionId?: string): { type: 'DESELECT_NODES'; ids: number[]; functionId?: string } => ({
    type: DESELECT_NODES,
    ids,
    functionId,
});

export const selectOnlyOneNode = (id: number, functionId?: string): { type: 'SELECT_ONLY_ONE_NODE'; id: number; functionId?: string } => ({
    type: SELECT_ONLY_ONE_NODE,
    id,
    functionId,
});

export const deselectAllNodes = (functionId?: string): { type: 'DESELECT_ALL_NODES'; functionId?: string } => ({
    type: DESELECT_ALL_NODES,
    functionId,
});

export const addVariable = (
    newVariable: GlueVariable,
): {
    type: 'ADD_VARIABLE';
    newVariable: GlueVariable;
} => ({
    type: ADD_VARIABLE,
    newVariable,
});

export const updateVariable = (
    oldName: string,
    variable: GlueVariable,
): {
    type: 'UPDATE_VARIABLE';
    oldName: string;
    variable: GlueVariable;
} => ({
    type: UPDATE_VARIABLE,
    oldName,
    variable,
});
export const deleteVariable = (name: string): { type: 'DELETE_VARIABLE'; name: string } => ({
    type: DELETE_VARIABLE,
    name,
});

export const setSelectedVariable = (
    name: string | undefined,
    nodeInstanceId: number,
    functionId?: string,
): { type: 'SET_SELECTED_VARIABLE'; name: string | undefined; nodeInstanceId: number; functionId?: string } => ({
    type: SET_SELECTED_VARIABLE,
    name,
    nodeInstanceId,
    functionId,
});

export type PortPositions = {
    [nodeInstanceId: number]: {
        inPortPositions: { [portName: string]: Vector };
        outPortPositions: { [portName: string]: Vector };
    };
};

export const layoutGraph = (
    functionId?: string,
    portPositions?: PortPositions,
): { type: 'LAYOUT_GRAPH'; functionId?: string; portPositions?: PortPositions } => ({
    type: LAYOUT_GRAPH,
    functionId,
    portPositions,
});

export const addFunction = (newFunction: FunctionDefinition): { type: 'ADD_FUNCTION'; newFunction: FunctionDefinition } => ({
    type: ADD_FUNCTION,
    newFunction,
});

export const updateFunction = (
    functionId: string,
    updatedFunction: FunctionDefinition,
): { type: 'UPDATE_FUNCTION'; functionId: string; updatedFunction: FunctionDefinition } => ({
    type: UPDATE_FUNCTION,
    functionId,
    updatedFunction,
});

export const updateFunctionName = (
    functionId: string,
    name: string,
): { type: 'UPDATE_FUNCTION_NAME'; functionId: string; name: string } => ({
    type: UPDATE_FUNCTION_NAME,
    functionId,
    name,
});

export const deleteFunction = (functionId: string): { type: 'DELETE_FUNCTION'; functionId: string } => ({
    type: DELETE_FUNCTION,
    functionId,
});

export const setFunctionPortValue = (
    nodeInstanceId: number,
    portName: string,
    referencedFunctionId: string | undefined,
    functionId?: string,
): {
    type: 'SET_FUNCTION_PORT_VALUE';
    nodeInstanceId: number;
    portName: string;
    referencedFunctionId: string | undefined;
    functionId?: string;
} => ({
    type: 'SET_FUNCTION_PORT_VALUE',
    nodeInstanceId,
    portName,
    referencedFunctionId,
    functionId,
});

export const createFunctionWithInstance = (
    functionName: string,
    position: Vector,
    parentFunctionId?: string,
): { type: 'CREATE_FUNCTION_WITH_INSTANCE'; functionName: string; position: Vector; functionId?: string } => ({
    type: CREATE_FUNCTION_WITH_INSTANCE,
    functionName,
    position,
    functionId: parentFunctionId,
});

export const createFunctionFromSignature = (
    functionName: string,
    inputPortDefinitions: FunctionPortDefinition[],
    outputPortDefinitions: FunctionPortDefinition[],
    nodeInstanceId: number,
    portName: string,
    parentFunctionId?: string,
): {
    type: 'CREATE_FUNCTION_FROM_SIGNATURE';
    functionName: string;
    inputPortDefinitions: FunctionPortDefinition[];
    outputPortDefinitions: FunctionPortDefinition[];
    nodeInstanceId: number;
    portName: string;
    functionId?: string;
} => ({
    type: CREATE_FUNCTION_FROM_SIGNATURE,
    functionName,
    inputPortDefinitions,
    outputPortDefinitions,
    nodeInstanceId,
    portName,
    functionId: parentFunctionId,
});

export const updateFunctionInputs = (
    functionId: string,
    inputPortDefinitions: FunctionPortDefinition[],
): {
    type: 'UPDATE_FUNCTION_INPUTS';
    functionId: string;
    inputPortDefinitions: FunctionPortDefinition[];
} => ({
    type: UPDATE_FUNCTION_INPUTS,
    functionId,
    inputPortDefinitions,
});

export const updateFunctionOutputs = (
    functionId: string,
    outputPortDefinitions: FunctionPortDefinition[],
): {
    type: 'UPDATE_FUNCTION_OUTPUTS';
    functionId: string;
    outputPortDefinitions: FunctionPortDefinition[];
} => ({
    type: UPDATE_FUNCTION_OUTPUTS,
    functionId,
    outputPortDefinitions,
});

export const addFunctionInstance = (
    functionId: string,
    position: Vector,
    parentFunctionId?: string,
): { type: 'ADD_FUNCTION_INSTANCE'; functionId: string; position: Vector; parentFunctionId?: string } => ({
    type: ADD_FUNCTION_INSTANCE,
    functionId,
    position,
    parentFunctionId,
});

export const setVariables = (variables: GlueVariable[]): { type: 'SET_VARIABLES'; variables: GlueVariable[] } => ({
    type: SET_VARIABLES,
    variables,
});

export const removeConnectionByEndpoints = (
    sendingNodeInstanceId: number,
    sendingPortName: string,
    receivingNodeInstanceId: number,
    receivingPortName: string,
    functionId?: string,
): {
    type: 'REMOVE_CONNECTION_BY_ENDPOINTS';
    sendingNodeInstanceId: number;
    sendingPortName: string;
    receivingNodeInstanceId: number;
    receivingPortName: string;
    functionId?: string;
} => ({
    type: REMOVE_CONNECTION_BY_ENDPOINTS,
    sendingNodeInstanceId,
    sendingPortName,
    receivingNodeInstanceId,
    receivingPortName,
    functionId,
});

export type GraphReducerActions =
    | ReturnType<typeof setNodeState>
    | ReturnType<typeof setNodeShape>
    | ReturnType<typeof moveNodes>
    | ReturnType<typeof moveNodesEnd>
    | ReturnType<typeof setNodeSelectedIntegrationAccess>
    | ReturnType<typeof addConnection>
    | ReturnType<typeof removeConnection>
    | ReturnType<typeof resizeNode>
    | ReturnType<typeof resizeNodeEnd>
    | ReturnType<typeof setNodeDimensions>
    | ReturnType<typeof addNode>
    | ReturnType<typeof mergeInGraph>
    | ReturnType<typeof removeNode>
    | ReturnType<typeof setPortValueAndType>
    | ReturnType<typeof setPortUserDefinedType>
    | ReturnType<typeof selectNodes>
    | ReturnType<typeof selectBranch>
    | ReturnType<typeof selectBranchAdd>
    | ReturnType<typeof deselectNodes>
    | ReturnType<typeof selectOnlyOneNode>
    | ReturnType<typeof deselectAllNodes>
    | ReturnType<typeof addVariable>
    | ReturnType<typeof updateVariable>
    | ReturnType<typeof deleteVariable>
    | ReturnType<typeof setSelectedVariable>
    | ReturnType<typeof layoutGraph>
    | ReturnType<typeof addFunction>
    | ReturnType<typeof updateFunction>
    | ReturnType<typeof updateFunctionName>
    | ReturnType<typeof deleteFunction>
    | ReturnType<typeof setFunctionPortValue>
    | ReturnType<typeof createFunctionWithInstance>
    | ReturnType<typeof createFunctionFromSignature>
    | ReturnType<typeof addFunctionInstance>
    | ReturnType<typeof updateFunctionInputs>
    | ReturnType<typeof updateFunctionOutputs>
    | ReturnType<typeof setVariables>
    | ReturnType<typeof removeConnectionByEndpoints>;
